import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { adminApi } from '../adminApi';
import { groupByPromptLabel } from '../evals/promptVersion';
import { GuideLink } from '../evals/GuideLink';
import { formatCost, formatDate, formatInt } from '../format';
import { NeoScentBadges, type NeoSessionRow } from '../components/NeoScentBadges';
import { StatusBadge } from '../components/StatusBadge';
import type { NeoRecallStats } from '../neoRecallStats';
import { NeoRecallStatsView } from './NeoRecallStatsView';

// Neo sessions browser (ADR 0119): a triage-first worklist over logged Neo chats.
// Defaults to the "Needs attention" segment so an operator opens the failures,
// not a wall of healthy rows. Mirrors the AdminManuals/AdminLlmUsage idiom
// (health strip → window → segment switcher → table) and reads NO message
// content — only server-computed scent flags (privacy + performance).
//
// Owners / Try it / All (TOM-950, ADR 0457) picks whose chats the strip and the
// list cover: owners by default, so real boats are triaged first; Try it is the
// visitors' chats, kept 90 days after their visit.
//
// "Past conversations" (TOM-255) is Neo's memory of owners' earlier chats, as
// counts across users only. Collapsed by default so the worklist stays in view;
// it loads, and is audited, when an operator opens it.

interface VersionRollup {
  promptVersion: string | null;
  turns: number;
  manualAttempted: number;
  grounded: number;
  truncated: number;
  thumbsDown: number;
}

interface Summary {
  windowDays: number;
  totals: {
    sessions: number;
    needsAttention: number;
    messages: number;
    refusals: number;
    thumbsDown: number;
    costUsd: number;
  };
  byVersion: VersionRollup[];
}

interface SessionList {
  sessions: NeoSessionRow[];
  nextCursor: string | null;
}

const WINDOWS = [7, 30, 90];

const AUDIENCES: Array<{ key: string; label: string }> = [
  { key: 'owners', label: 'Owners' },
  { key: 'try_it', label: 'Try it' },
  { key: 'all', label: 'All' },
];

const SEGMENTS: Array<{ key: string; label: string }> = [
  { key: 'needs_attention', label: 'Needs attention' },
  { key: 'refused', label: 'Refused' },
  { key: 'ungrounded', label: 'Ungrounded' },
  { key: 'thumbs_down', label: 'Thumbs-down' },
  { key: 'truncated', label: 'Truncated' },
  { key: 'errors', label: 'Errors' },
  { key: 'all', label: 'All' },
];

function pct(n: number, d: number): string {
  if (d === 0) return '—';
  return `${Math.round((n / d) * 100)}%`;
}

/** A label's rows added up, for its line above its versions (cloud ADR 0332). */
function sumRollups(label: string | null, rows: readonly VersionRollup[]): VersionRollup {
  const sum = (pick: (v: VersionRollup) => number) => rows.reduce((n, v) => n + pick(v), 0);
  return {
    promptVersion: label,
    turns: sum((v) => v.turns),
    manualAttempted: sum((v) => v.manualAttempted),
    grounded: sum((v) => v.grounded),
    truncated: sum((v) => v.truncated),
    thumbsDown: sum((v) => v.thumbsDown),
  };
}

/**
 * One line of "Quality by release". A label with one version is one line; a
 * label with several (fingerprints, or a row from before ADR 0332 beside
 * them) leads with its total, and each exact version follows, indented.
 */
function VersionRow({ v, name, sub }: { v: VersionRollup; name?: ReactNode; sub?: boolean }) {
  return (
    <tr className={sub === true ? 'admin-version-sub' : undefined}>
      <td>{name ?? (v.promptVersion !== null ? <code>{v.promptVersion}</code> : '—')}</td>
      <td>{formatInt(v.turns)}</td>
      <td>
        {pct(v.grounded, v.manualAttempted)}{' '}
        <span className="admin-muted">
          ({formatInt(v.grounded)}/{formatInt(v.manualAttempted)})
        </span>
      </td>
      <td>{pct(v.truncated, v.turns)}</td>
      <td>{pct(v.thumbsDown, v.turns)}</td>
    </tr>
  );
}

export function AdminNeoSessions() {
  const [days, setDays] = useState(30);
  const [segment, setSegment] = useState('needs_attention');
  const [audience, setAudience] = useState('owners');
  const [summary, setSummary] = useState<Summary | null>(null);
  const [list, setList] = useState<SessionList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showRecall, setShowRecall] = useState(false);
  const [recall, setRecall] = useState<NeoRecallStats | null>(null);
  const [recallError, setRecallError] = useState<string | null>(null);

  // Summary follows the window and the audience.
  useEffect(() => {
    let ignore = false;
    setSummary(null);
    adminApi<Summary>(`/v1/admin/neo/summary?days=${days}&audience=${audience}`)
      .then((res) => {
        if (!ignore) setSummary(res);
      })
      .catch(() => {
        /* the list error banner is enough; the strip just stays blank */
      });
    return () => {
      ignore = true;
    };
  }, [days, audience]);

  // The past-conversation numbers follow the window, once the section is open.
  useEffect(() => {
    if (!showRecall) return;
    let ignore = false;
    setRecall(null);
    setRecallError(null);
    adminApi<NeoRecallStats>(`/v1/admin/neo/recall-stats?days=${days}`)
      .then((res) => {
        if (!ignore) setRecall(res);
      })
      .catch((err) => {
        if (ignore) return;
        const e = err as { body?: { message?: string } };
        setRecallError(e.body?.message ?? 'Failed to load the past-conversation numbers.');
      });
    return () => {
      ignore = true;
    };
  }, [days, showRecall]);

  // The list follows window + audience + segment.
  useEffect(() => {
    let ignore = false;
    setList(null);
    setError(null);
    adminApi<SessionList>(
      `/v1/admin/neo/sessions?days=${days}&audience=${audience}&segment=${segment}`
    )
      .then((res) => {
        if (!ignore) setList(res);
      })
      .catch((err) => {
        if (ignore) return;
        const e = err as { body?: { message?: string } };
        setError(e.body?.message ?? 'Failed to load Neo sessions.');
      });
    return () => {
      ignore = true;
    };
  }, [days, audience, segment]);

  async function loadMore() {
    if (list === null || list.nextCursor === null || loadingMore) return;
    setLoadingMore(true);
    try {
      const next = await adminApi<SessionList>(
        `/v1/admin/neo/sessions?days=${days}&audience=${audience}&segment=${segment}&cursor=${encodeURIComponent(list.nextCursor)}`
      );
      setList((prev) =>
        prev === null
          ? next
          : { sessions: [...prev.sessions, ...next.sessions], nextCursor: next.nextCursor }
      );
    } catch {
      /* keep what we have */
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <section className="admin-section">
      <header className="admin-section-header">
        <h1>Neo chats</h1>
        <span className="admin-count admin-seg">
          {WINDOWS.map((w) => (
            <button
              key={w}
              type="button"
              className="admin-link-button"
              disabled={w === days}
              onClick={() => setDays(w)}
            >
              {w}d
            </button>
          ))}
        </span>
        <GuideLink section="fix-find" />
      </header>
      <div className="admin-count admin-seg" role="tablist" aria-label="Whose chats">
        {AUDIENCES.map((a) => (
          <button
            key={a.key}
            type="button"
            role="tab"
            aria-selected={a.key === audience}
            className="admin-link-button"
            disabled={a.key === audience}
            onClick={() => setAudience(a.key)}
          >
            {a.label}
          </button>
        ))}
      </div>
      <p className="admin-hint">
        {audience === 'try_it'
          ? `Try it visitors' Neo conversations over the last ${days} days, kept 90 days after each visit. `
          : `Logged Neo conversations over the last ${days} days. `}
        Open a session to see the full transcript (that access is logged). Start with what needs
        attention.
      </p>

      {summary !== null ? (
        <>
          <dl className="admin-defs">
            <dt>Needs attention</dt>
            <dd className="admin-defs-lead">{formatInt(summary.totals.needsAttention)}</dd>
            <dt>Sessions</dt>
            <dd>{formatInt(summary.totals.sessions)}</dd>
            <dt>Turns</dt>
            <dd>{formatInt(summary.totals.messages)}</dd>
            <dt>Refusals</dt>
            <dd>{formatInt(summary.totals.refusals)}</dd>
            <dt>Thumbs-down</dt>
            <dd>{formatInt(summary.totals.thumbsDown)}</dd>
            <dt>Cost</dt>
            <dd>{formatCost(summary.totals.costUsd)}</dd>
          </dl>

          {summary.byVersion.length > 0 ? (
            <>
              <h2>Quality by release</h2>
              <p className="admin-hint">
                A prompt version is a label and a fingerprint of the prompt and tools, such as
                v61+3f9a2c1d. Versions under one label are added up on the label’s line, and each
                follows it.
              </p>
              <div className="scroll-x">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Prompt version</th>
                      <th>Turns</th>
                      <th>Grounded</th>
                      <th>Truncated</th>
                      <th>Thumbs-down</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupByPromptLabel(summary.byVersion, (v) => v.promptVersion).map((g) =>
                      g.rows.length === 1 ? (
                        <VersionRow key={g.label ?? 'none'} v={g.rows[0]!} />
                      ) : (
                        <Fragment key={g.label ?? 'none'}>
                          <VersionRow
                            v={sumRollups(g.label, g.rows)}
                            name={
                              <>
                                <strong>{g.label}</strong>{' '}
                                <span className="admin-muted">· {g.rows.length} versions</span>
                              </>
                            }
                          />
                          {g.rows.map((v) => (
                            <VersionRow key={v.promptVersion ?? 'none'} v={v} sub />
                          ))}
                        </Fragment>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </>
      ) : null}

      <h2>
        Past conversations{' '}
        <button
          type="button"
          className="admin-link-button"
          aria-expanded={showRecall}
          onClick={() => setShowRecall((v) => !v)}
        >
          {showRecall ? 'Hide' : 'Show'}
        </button>
      </h2>
      {showRecall ? (
        recallError !== null ? (
          <div className="admin-auth-banner error">{recallError}</div>
        ) : recall === null ? (
          <p className="admin-loading-row">Loading…</p>
        ) : (
          <NeoRecallStatsView stats={recall} />
        )
      ) : null}

      {/* A heading of its own, so the segments do not read as part of the
          section above them. */}
      <h2>Sessions</h2>
      <div className="admin-count admin-seg" role="tablist" aria-label="Session segments">
        {SEGMENTS.map((s) => (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={s.key === segment}
            className="admin-link-button"
            disabled={s.key === segment}
            onClick={() => setSegment(s.key)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {error !== null ? <div className="admin-auth-banner error">{error}</div> : null}
      {list === null && error === null ? <p className="admin-loading-row">Loading…</p> : null}

      {list !== null ? (
        list.sessions.length === 0 ? (
          <p className="admin-empty">
            {segment === 'needs_attention'
              ? 'Nothing needs attention. Neo is healthy.'
              : 'No sessions in this segment and window.'}
          </p>
        ) : (
          <>
            <div className="scroll-x">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Last active</th>
                    <th>User</th>
                    <th>Turns</th>
                    <th>Cost</th>
                    <th>Signals</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {list.sessions.map((s) => (
                    <tr key={s.id}>
                      <td>{formatDate(s.last_message_at)}</td>
                      <td>
                        <Link to={`/admin/users/${s.user_id}`}>
                          <code>{s.short_id}</code>
                        </Link>
                        {s.try_it === true ? (
                          <>
                            {' '}
                            <StatusBadge status="ai" label="Try it" />
                          </>
                        ) : null}
                      </td>
                      <td>{formatInt(s.message_count)}</td>
                      <td>{formatCost(s.total_cost_usd)}</td>
                      <td>
                        <NeoScentBadges row={s} />
                      </td>
                      <td>
                        <Link to={`/admin/neo/sessions/${s.id}`}>Open transcript →</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {list.nextCursor !== null ? (
              <p>
                <button
                  type="button"
                  className="admin-link-button"
                  onClick={() => void loadMore()}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading…' : 'Load more'}
                </button>
              </p>
            ) : null}
          </>
        )
      ) : null}
    </section>
  );
}
